import { useCallback } from 'react';

import {
  GridColumnResizeParams,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro';

import { LocalStoragePrefix } from '@/constants';
import { setLocalStorageItem } from '@/utils';

import { DataGridColSettings } from '../utils';

export const useGridColumns = (
  columnSettings: DataGridColSettings[],
  setColumnSettings: (settings: DataGridColSettings[]) => void,
  name: string,
  userId: string,
  hiddenColumnCount: number,
) => {
  const handleColumnChange = useCallback(
    (column: { oldIndex: number; targetIndex: number }) => {
      const newColumnOrder = [...columnSettings];
      const [movedColumn] = newColumnOrder.splice(
        column.oldIndex - hiddenColumnCount,
        1,
      );

      newColumnOrder.splice(
        column.targetIndex - hiddenColumnCount,
        0,
        movedColumn,
      );

      setColumnSettings(newColumnOrder);
      setLocalStorageItem({
        prefix: `${name}-${LocalStoragePrefix.TableSetting}`,
        key: userId,
        value: newColumnOrder,
      });
    },
    [columnSettings, userId, name, setColumnSettings, hiddenColumnCount],
  );

  const handleColumnVisibility = useCallback(
    (model: Record<string, boolean>) => {
      const updatedSettings = columnSettings.map((column) => ({
        ...column,
        visible: model[column.field],
      }));

      setColumnSettings(updatedSettings);
      setLocalStorageItem({
        prefix: `${name}-${LocalStoragePrefix.TableSetting}`,
        key: userId,
        value: updatedSettings,
      });
    },
    [columnSettings, userId, name, setColumnSettings],
  );

  const handleColumnWidthChange = useCallback(
    ({ colDef, width, element }: GridColumnResizeParams) => {
      const columnIndex = columnSettings.findIndex(
        (column) =>
          column.field === colDef.field ||
          column.headerName === colDef.headerName,
      );

      if (columnIndex < 0) {
        return;
      }

      const minWidth = colDef.minWidth ?? 50;
      const maxWidth = colDef.maxWidth ?? Number.POSITIVE_INFINITY;
      const nextWidth = Math.min(Math.max(width, minWidth), maxWidth);

      const updatedSettings = columnSettings.map((column, index) =>
        index === columnIndex ? { ...column, width: nextWidth } : column,
      );

      setColumnSettings(updatedSettings);
      setLocalStorageItem({
        prefix: `${name}-${LocalStoragePrefix.TableSetting}`,
        key: userId,
        value: updatedSettings,
      });

      const visibleColumns = updatedSettings.filter((column) => column.visible);
      const resizedColumn = updatedSettings[columnIndex];
      const isLastVisibleColumn =
        visibleColumns[visibleColumns.length - 1]?.field ===
        resizedColumn.field;

      if (isLastVisibleColumn) {
        requestAnimationFrame(() => {
          const gridRoot = element?.closest('.MuiDataGrid-root');
          const virtualScroller = gridRoot?.querySelector(
            '.MuiDataGrid-virtualScroller',
          ) as HTMLElement | null;

          if (virtualScroller) {
            virtualScroller.scrollLeft = virtualScroller.scrollWidth;
          }
        });
      }
    },
    [columnSettings, userId, name, setColumnSettings],
  );

  const convertToKeyVisibilityMap = useCallback(
    (items: DataGridColSettings[]): GridColumnVisibilityModel => {
      return items
        .filter((column) => column && column.field)
        .reduce(
          (acc, column) => ({
            ...acc,
            [column.field]: column.visible,
          }),
          {} as GridColumnVisibilityModel,
        );
    },
    [],
  );

  return {
    handleColumnChange,
    handleColumnVisibility,
    handleColumnWidthChange,
    convertToKeyVisibilityMap,
  };
};
